import type { CellValue, KpiViewType, MetricType } from './types';

const NUMBER_FORMATTER = new Intl.NumberFormat('en-US', { maximumFractionDigits: 2 });

export const formatNumber = (value: number | null | undefined): string => {
    if (value === null || value === undefined || Number.isNaN(value)) return '--';
    return NUMBER_FORMATTER.format(value);
};

export const renderNumberCell = (value: number | null | undefined) => (
    <span>{formatNumber(value)}</span>
);

interface PromoCellProps {
    value: CellValue | undefined;
    kpiViewType: KpiViewType;
    metricType: MetricType;
}

const isComparisonView = (kvt: KpiViewType): boolean => kvt === 'actuals_vs_pre_snapshot';

const formatChange = (change: number | null, metricType: MetricType): string => {
    if (change === null || change === undefined || Number.isNaN(change)) return '--';
    const formatted = NUMBER_FORMATTER.format(Math.abs(change));
    return metricType === 'percent' ? `${formatted}%` : formatted;
};

const arrowFor = (change: number | null): string => {
    if (change === null || change === undefined) return '';
    if (change > 0) return '\u2191';
    if (change < 0) return '\u2193';
    return '';
};

const colourFor = (change: number | null): string => {
    if (change === null || change === undefined || change === 0) return '#52525C';
    return change > 0 ? '#15803D' : '#B91C1C';
};

export const PromoCell = ({ value, kpiViewType, metricType }: PromoCellProps) => {
    if (!value) return <span>--</span>;
    if (!isComparisonView(kpiViewType)) {
        return <span>{formatNumber(value.current)}</span>;
    }

    const arrow = arrowFor(value.change);
    return (
        <div className="flex items-center justify-end gap-x-3">
            <span className="text-[#18181B] font-medium">{formatNumber(value.current)}</span>
            <span
                style={{ color: colourFor(value.change) }}
                className="font-medium inline-flex items-center gap-x-1"
            >
                {value.change === null
                    ? '--'
                    : `${formatNumber(Math.abs(value.change))}${metricType === 'percent' ? '%' : ''}`}
                {arrow && <span>{arrow}</span>}
            </span>
            <span className="text-[#A1A1AA]">{formatNumber(value.previous)}</span>
        </div>
    );
};

PromoCell.displayName = 'PromoCell';

// Used for static plan-metric tooltip strings (non-component).
export { formatChange };
